import { Box, Tooltip as ChakraTooltip } from "@chakra-ui/react";
import { useRestakeApy } from "@hooks/useRestakeApy";
import useTVL from "@hooks/useTVL";
import { Typography } from "@kelp-dao/kernel-design-system";
import React, { useState, useEffect } from "react";
import { numberToCurrency } from "utils";
import { TStatsCardProps } from "./types";

const HeaderStats = () => {
  const tvl = useTVL();
  const { kelpAPY, eigenAPY, roundedTotalAPY, is7DayApy } = useRestakeApy();

  const headerStats = [
    {
      label: "TVL",
      value: tvl?.tvl?.usdTvl
        ? `$${numberToCurrency(tvl.tvl.usdTvl.toString())}`
        : "-",
      variant: "default" as const,
      justify: "justify-start",
    },
    {
      label: "APR",
      value: roundedTotalAPY,
      variant: "default" as const,
      justify: "justify-end",
    },
  ];

  return (
    <StatsCard
      stats={headerStats as TStatsCardProps["stats"]}
      stakingApy={kelpAPY}
      restakingApy={eigenAPY}
      is7DayApy={is7DayApy}
    />
  );
};

export default HeaderStats;

const StatsCard: React.FC<TStatsCardProps> = ({
  stats,
  stakingApy,
  restakingApy,
  is7DayApy,
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [autoCloseTimer, setAutoCloseTimer] = useState<number | null>(null);

  const getVariantClass = (variant: string = "default") => {
    switch (variant) {
      case "success":
        return "text-feedback-success";
      case "warning":
        return "text-feedback-warning";
      case "error":
        return "text-feedback-error";
      default:
        return "text-text-primary";
    }
  };

  const handleTooltipClick = () => {
    // Clear any existing timer
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
    }

    setIsTooltipOpen(true);
    const timer = setTimeout(() => {
      setIsTooltipOpen(false);
      setAutoCloseTimer(null);
    }, 2000) as unknown as number;
    setAutoCloseTimer(timer);
  };

  const handleMouseEnter = () => {
    // Only open on hover if not on a touch device (better for desktop)
    setIsTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    // Only close on mouse leave if there's no active auto-close timer
    if (!autoCloseTimer) {
      setIsTooltipOpen(false);
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
      }
    };
  }, [autoCloseTimer]);

  return (
    <div className={"flex flex-row gap-5 text-center lg:text-left"}>
      {stats.map((stat, index) => (
        <div key={index}>
          <Typography
            type="body"
            variant="small"
            className="text-text-secondary mb-0.5 text-[12px] 2xl:text-[14px] text-left"
          >
            {stat.label}
          </Typography>
          <div className={`flex items-center ${stat.justify} gap-2`}>
            {stat.label === "APR" ? (
              <ChakraTooltip
                placement="bottom-end"
                bg="backgroundCard"
                color="textSecondary"
                fontSize="12px"
                p="8px"
                borderRadius="8px"
                isOpen={isTooltipOpen}
                closeOnClick={false}
                label={
                  <div className="flex flex-col gap-2 p-3 rounded-lg">
                    <Box
                      borderBottom="1px dashed"
                      borderColor="text.secondary"
                      pb="8px"
                      fontSize="12px"
                      fontWeight="400"
                      color="#4A6A6A"
                    >
                      {`${is7DayApy ? "7-day" : "30-day"} moving average`}
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      gap={3}
                      fontSize="12px"
                      fontWeight="400"
                      color="#4A6A6A"
                    >
                      <Box>Staking APR:</Box>
                      <Box>{stakingApy?.toFixed(1)}%</Box>
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      gap={3}
                      fontSize="12px"
                      fontWeight="400"
                      color="#4A6A6A"
                    >
                      <Box>Restaking APR:</Box>
                      <Box>{restakingApy?.toFixed(1)}%</Box>
                    </Box>
                  </div>
                }
              >
                <span
                  className="inline-block cursor-pointer"
                  onClick={handleTooltipClick}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <Typography
                    type="heading"
                    variant="xsmall"
                    className={`${getVariantClass(
                      stat.variant
                    )} underline decoration-dotted underline-offset-8 text-[16px] 2xl:text-[20px]`}
                  >
                    {stat.value}
                  </Typography>
                </span>
              </ChakraTooltip>
            ) : (
              <Typography
                type="heading"
                variant="xsmall"
                className={`${getVariantClass(
                  stat.variant
                )} text-[16px] 2xl:text-[20px]`}
              >
                {stat.value}
              </Typography>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
